import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { usePageMeta } from '../../hooks/usePageMeta';
import { cSuiteRoles, execCapabilities, execStages } from '../../data/executiveSearch';
import { images } from '../../data/images';
import { PageHero } from '../../components/ui/PageHero';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { Button } from '../../components/ui/Button';
import { Reveal } from '../../components/ui/Reveal';
import { DiamondStack } from '../../components/visuals/DiamondStack';
import { EmployerInquiryForm } from '../../components/forms/EmployerInquiryForm';
export function ExecutiveSearch() {
  usePageMeta('Executive Search', 'Retained, confidential executive search for CEO, CFO, COO, CTO, CHRO, board and senior leadership appointments.');
  const [stage, setStage] = useState(0);
  return <>
      <PageHero crumbs={[{
      label: 'Services',
      to: '/services'
    }, {
      label: 'Executive Search'
    }]} kicker="Executive search" title="Begin a Confidential Search." intro="Senior leadership appointments define an organisation’s direction. Our executive search practice brings research, judgement and discretion to every one." actions={<>
            <Button to="#inquiry" size="lg" arrow data-id="element-1100" data-mp="u032pxibzh39">
              Start a search conversation
            </Button>
            <Button to="/process" size="lg" variant="outlineLight" data-id="element-1101" data-mp="17z2nuv6mxduo">
              Our process
            </Button>
          </>} aside={<DiamondStack data-id="element-1102" data-mp="13xqxgnfxsolf" />} data-id="element-1099" data-mp="2363zlbg5m7uh" />

      <section className="bg-ivory py-24" data-id="element-1103" data-mp="1hpe5fkj8rlk1">
        <div className="container-page grid gap-14 lg:grid-cols-12" data-id="element-1104" data-mp="1hocleqk8zoq6">
          <div className="lg:col-span-5" data-id="element-1105" data-mp="1cpsv6dymfqs0">
            <SectionHeading kicker="C-suite search" title="Enterprise leadership, appointed with care." intro="We work directly with chairs, boards and chief executives on the appointments that shape strategy and culture." data-id="element-1106" data-mp="1cquhxfp021pk" />
          </div>
          <dl className="divide-y divide-line border-y border-line lg:col-span-7" data-id="element-1107" data-mp="1tt03x8m2hne2">
            {cSuiteRoles.map(r => <div key={r.role} className="grid gap-2 py-5 sm:grid-cols-[220px_1fr] sm:gap-6" data-id="element-1108" data-mp="1yop5tilk7gdj">
                <dt className="font-display text-xl font-semibold text-ink" data-id="element-1109" data-mp="23nrys4o9lw55">{r.role}</dt>
                <dd className="leading-relaxed text-muted" data-id="element-1110" data-mp="28hw6wr99oa7t">{r.focus}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      <section className="bg-white py-24" data-id="element-1111" data-mp="2hgqax2oxg2ve">
        <div className="container-page" data-id="element-1112" data-mp="2hfonrzumzpxb">
          <SectionHeading kicker="Capabilities" title="Everything a leadership search requires." data-id="element-1113" data-mp="2mgclt5ha9711" />
          <div className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1114" data-mp="2rffleaehul1m">
            {execCapabilities.map((c, i) => <Reveal key={c.title} delay={i % 3 * 0.04} className="border-t border-line pt-6" data-id="element-1115" data-mp="s3d1x05vdqny" data-mpi={i}>
                <h3 className="font-display text-2xl font-semibold text-ink" data-id="element-1116" data-mp="x2h9xnh25icp">{c.title}</h3>
                <p className="mt-2 leading-relaxed text-muted" data-id="element-1117" data-mp="pi5ebzm6c5e2">{c.text}</p>
              </Reveal>)}
          </div>
        </div>
      </section>

      <section className="bg-midnight py-24 text-white" data-id="element-1118" data-mp="kpc4wyfrg8vn">
        <div className="container-page" data-id="element-1119" data-mp="fsviq9s4g4l7">
          <SectionHeading tone="dark" kicker="The executive search process" title="From initial briefing to appointment." data-id="element-1120" data-mp="fpr4skoutuhc" />
          <div className="mt-12 grid gap-10 lg:grid-cols-12" data-id="element-1121" data-mp="18havsqrp0ctw">
            <ol className="space-y-1 lg:col-span-5" role="tablist" aria-label="Executive search stages" data-id="element-1122" data-mp="18eongowz9tam">
              {execStages.map((s, i) => <li key={s.title} data-id="element-1123" data-mp="yhsdeerfkz3y" data-mpi={i}>
                  <button type="button" role="tab" aria-selected={stage === i} aria-controls="exec-stage-panel" onClick={() => setStage(i)} className={`flex w-full items-center gap-4 rounded-md px-4 py-3.5 text-left transition-colors duration-150 ${stage === i ? 'bg-white/10' : 'hover:bg-white/5'}`} data-id="element-1124" data-mp="ygqr1e4ysq6x">
                    <span className={`font-display text-xl ${stage === i ? 'text-gold-300' : 'text-white/50'}`} data-id="element-1125" data-mp="1bqsluww3diwv">{i + 1}</span>
                    <span className={`font-medium ${stage === i ? 'text-white' : 'text-white/75'}`} data-id="element-1126" data-mp="wy8o6brpo3mt">{s.title}</span>
                    <span className="ml-auto h-1 w-16 overflow-hidden rounded-full bg-white/10" aria-hidden="true" data-id="element-1127" data-mp="1pp0onlft7efy">
                      <span className="block h-full bg-gold transition-[width] duration-200" style={{
                    width: i <= stage ? '100%' : '0%'
                  }} data-id="element-1128" data-mp="1uon481j416rx" />
                    </span>
                  </button>
                </li>)}
            </ol>
            <div id="exec-stage-panel" role="tabpanel" className="relative overflow-hidden rounded-xl lg:col-span-7" data-id="element-1129" data-mp="1kocrsj4wg3dt">
              <img src={images.leadersMeeting} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" loading="lazy" data-id="element-1130" data-mp="4ds5s7nzrgqe" />
              <div className="relative flex min-h-[320px] flex-col justify-end bg-midnight-800/70 p-8 md:p-10" data-id="element-1131" data-mp="2ni0agnov8w8o">
                <AnimatePresence mode="wait" data-id="element-1132" data-mp="2iidnvnn5e3n7">
                  <motion.div key={stage} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.2,
                  ease: [0.23, 1, 0.32, 1]
                }} data-id="element-1133" data-mp="2iljgvdywdpo7">
                    <p className="text-sm text-gold-300" data-id="element-1134" data-mp="1zsxm6wzq8557">
                      Stage {stage + 1} of {execStages.length}
                    </p>
                    <h3 className="mt-2 font-display text-4xl font-semibold" data-id="element-1135" data-mp="2dldqs28a0b88">{execStages[stage].title}</h3>
                    <p className="mt-4 max-w-lg text-lg leading-relaxed text-white/80" data-id="element-1136" data-mp="28su50hzfq9sc">{execStages[stage].text}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="inquiry" className="bg-ivory py-24" data-id="element-1137" data-mp="3g7hamv5bj3nd">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-1138" data-mp="3g6xtvhremlax">
          <div className="lg:col-span-4" data-id="element-1139" data-mp="3l593brdsetsm">
            <SectionHeading kicker="Client inquiry" title="Start a confidential conversation." data-id="element-1140" data-mp="3l47pxg9atv7q" />
            <p className="mt-5 leading-relaxed text-muted" data-id="element-1141" data-mp="3pz3utgzmwd29">
              Share as much or as little as you are comfortable with. A consultant will treat your inquiry in confidence.
            </p>
          </div>
          <div className="rounded-xl border border-line bg-white p-6 shadow-soft md:p-10 lg:col-span-8" data-id="element-1142" data-mp="2xaptar9c6yly">
            <EmployerInquiryForm variant="executive" submitLabel="Begin a Confidential Search" data-id="element-1143" data-mp="376j6mcuvqw4s" />
          </div>
        </div>
      </section>
    </>;
}